/** The simulation world: balls + table geometry + the per-step update loop. */
import {
  ballsFor,
  BALL_BY_VARIANT,
  PHYSICS,
  startPositions,
  tableFor,
  USE_CCD,
  type BallId,
  type PhysicsConstants,
  type VariantId,
} from '../config';
import type { BallSnapshot, CushionId, GameEvent, Vec3 } from '../types';
import { resolveBallBall, resolveCushion } from './collide';
import { makeBall, toSnapshot, type Ball, type Frame } from './model';
import { stepBallMotion as stepMotion } from './step';

interface CushionDef {
  id: CushionId;
  n: Vec3; // inward normal
  t: Vec3; // in-plane tangent along the rail
  axis: 'x' | 'y';
  sign: 1 | -1; // which side
}

const CUSHIONS: CushionDef[] = [
  { id: 0, n: { x: -1, y: 0, z: 0 }, t: { x: 0, y: 1, z: 0 }, axis: 'x', sign: 1 },
  { id: 1, n: { x: 1, y: 0, z: 0 }, t: { x: 0, y: 1, z: 0 }, axis: 'x', sign: -1 },
  { id: 2, n: { x: 0, y: -1, z: 0 }, t: { x: 1, y: 0, z: 0 }, axis: 'y', sign: 1 },
  { id: 3, n: { x: 0, y: 1, z: 0 }, t: { x: 1, y: 0, z: 0 }, axis: 'y', sign: -1 },
];

export interface WorldOptions {
  playWidth: number;
  playLength: number;
  constants: PhysicsConstants;
  balls: { id: BallId; radius: number; mass: number; pos: { x: number; y: number } }[];
  /** Use continuous-collision detection (exact contact ordering) instead of the plain fixed step. */
  ccd?: boolean;
}

export class World {
  readonly constants: PhysicsConstants;
  readonly halfW: number;
  readonly halfL: number;
  readonly balls: Ball[];
  readonly ccd: boolean;
  private readonly byId: Map<BallId, Ball>;

  constructor(opts: WorldOptions) {
    this.constants = opts.constants;
    this.halfW = opts.playWidth / 2;
    this.halfL = opts.playLength / 2;
    this.ccd = opts.ccd ?? false;
    this.balls = opts.balls.map((b) => makeBall(b.id, b.radius, b.mass, b.pos.x, b.pos.y));
    this.byId = new Map(this.balls.map((b) => [b.id, b]));
  }

  get(id: BallId): Ball {
    const b = this.byId.get(id);
    if (!b) throw new Error(`no ball ${id}`);
    return b;
  }

  clone(): World {
    const w = new World({
      playWidth: this.halfW * 2,
      playLength: this.halfL * 2,
      constants: this.constants,
      ccd: this.ccd,
      balls: this.balls.map((b) => ({ id: b.id, radius: b.radius, mass: b.mass, pos: { x: b.pos.x, y: b.pos.y } })),
    });
    for (const src of this.balls) {
      const dst = w.get(src.id);
      dst.pos = { ...src.pos };
      dst.vel = { ...src.vel };
      dst.angVel = { ...src.angVel };
      dst.orientation = { ...src.orientation };
      dst.state = src.state;
    }
    return w;
  }

  /** Advance the whole world by `dt`. `t` is the sim time at the START of the step. */
  step(dt: number, t: number): GameEvent[] {
    return this.ccd ? this.stepCcd(dt, t) : this.stepFixed(dt, t);
  }

  /** Plain fixed step: advance all balls by `dt`, then resolve any contacts. */
  private stepFixed(dt: number, t: number): GameEvent[] {
    const events = this.advanceMotion(dt, t + dt);
    events.push(...this.resolveContacts(t + dt));
    return events;
  }

  /**
   * Continuous-collision step: repeatedly advance only up to the exact time of
   * the next contact, resolve it, and continue with the remaining time. Contacts
   * are handled in true time order with no penetration. Reuses the same motion
   * and collision resolvers as the fixed step.
   */
  private stepCcd(dt: number, t: number): GameEvent[] {
    const events: GameEvent[] = [];
    let remaining = dt;
    let elapsed = 0;
    let guard = 0;
    while (remaining > 1e-9 && guard++ < 24) {
      const tc = this.timeToNextContact(remaining);
      const adv = Math.min(tc, remaining);
      events.push(...this.advanceMotion(adv, t + elapsed + adv));
      elapsed += adv;
      remaining -= adv;
      events.push(...this.resolveContacts(t + elapsed));
    }
    return events;
  }

  /** Step every ball's free motion by `dt`; emit transition events at `tEnd`. */
  private advanceMotion(dt: number, tEnd: number): GameEvent[] {
    const events: GameEvent[] = [];
    for (const b of this.balls) {
      const prev = b.state;
      const next = stepMotion(b, this.constants, dt);
      if (next !== prev) events.push({ kind: 'transition', t: tEnd, ball: b.id, from: prev, to: next });
    }
    return events;
  }

  /** Resolve any current cushion / ball-ball overlaps at time `tEnd`. */
  private resolveContacts(tEnd: number): GameEvent[] {
    const c = this.constants;
    const events: GameEvent[] = [];

    for (const b of this.balls) {
      for (const cu of CUSHIONS) {
        const lim = (cu.axis === 'x' ? this.halfW : this.halfL) - b.radius;
        const coord = cu.axis === 'x' ? b.pos.x : b.pos.y;
        if (cu.sign === 1 ? coord > lim : coord < -lim) {
          const hit = resolveCushion(b, cu.n, cu.t, c);
          if (cu.axis === 'x') b.pos.x = cu.sign === 1 ? lim : -lim;
          else b.pos.y = cu.sign === 1 ? lim : -lim;
          if (hit) events.push({ kind: 'ball-cushion', t: tEnd, ball: b.id, cushion: cu.id, pos: { ...b.pos }, speed: hit });
        }
      }
    }

    for (let i = 0; i < this.balls.length; i++) {
      for (let j = i + 1; j < this.balls.length; j++) {
        const a = this.balls[i];
        const b = this.balls[j];
        // Full 3D overlap test so an airborne ball still strikes others at the
        // right height — only a high-enough hop (z gap > 2R) clears them.
        const dx = b.pos.x - a.pos.x;
        const dy = b.pos.y - a.pos.y;
        const dz = b.pos.z - a.pos.z;
        const dist = Math.hypot(dx, dy, dz);
        const minDist = a.radius + b.radius;
        if (dist < minDist && dist > 1e-9) {
          const hit = resolveBallBall(a, b, c);
          const push = (minDist - dist) / 2 + 1e-6;
          const nx = dx / dist;
          const ny = dy / dist;
          const nz = dz / dist;
          a.pos.x -= nx * push;
          a.pos.y -= ny * push;
          a.pos.z -= nz * push;
          b.pos.x += nx * push;
          b.pos.y += ny * push;
          b.pos.z += nz * push;
          if (a.pos.z < a.radius) a.pos.z = a.radius; // never sink below the cloth
          if (b.pos.z < b.radius) b.pos.z = b.radius;
          if (hit) events.push({ kind: 'ball-ball', t: tEnd, a: a.id, b: b.id, pos: { x: (a.pos.x + b.pos.x) / 2, y: (a.pos.y + b.pos.y) / 2, z: a.radius }, speed: hit });
        }
      }
    }

    return events;
  }

  /**
   * Time (s) until the next ball-ball or ball-cushion contact within `maxT`,
   * using a constant-velocity approximation over the short window (Infinity if
   * none). Lets the CCD step land exactly on contacts.
   */
  private timeToNextContact(maxT: number): number {
    let best = Infinity;
    const eps = 1e-9;
    // aim a hair past the contact so the strict overlap checks in resolveContacts fire
    const GAP = 1e-5;

    // ball-cushion (linear: coord(s) = coord + vel*s reaches just past the rail limit)
    for (const b of this.balls) {
      for (const cu of CUSHIONS) {
        const lim = (cu.axis === 'x' ? this.halfW : this.halfL) - b.radius;
        const coord = cu.axis === 'x' ? b.pos.x : b.pos.y;
        const vel = cu.axis === 'x' ? b.vel.x : b.vel.y;
        const target = cu.sign === 1 ? lim + GAP : -lim - GAP;
        if (cu.sign === 1 ? vel > eps : vel < -eps) {
          const s = (target - coord) / vel;
          if (s > eps && s <= maxT) best = Math.min(best, s);
        }
      }
    }

    // ball-ball (quadratic: |d + w*s| = R1+R2, take the entry root, slightly penetrated)
    for (let i = 0; i < this.balls.length; i++) {
      for (let j = i + 1; j < this.balls.length; j++) {
        const a = this.balls[i];
        const b = this.balls[j];
        if (a.pos.z > a.radius + 1e-3 || b.pos.z > b.radius + 1e-3) continue;
        const dx = b.pos.x - a.pos.x;
        const dy = b.pos.y - a.pos.y;
        const wx = b.vel.x - a.vel.x;
        const wy = b.vel.y - a.vel.y;
        const minDist = a.radius + b.radius - GAP;
        const qa = wx * wx + wy * wy;
        if (qa < eps) continue;
        const qb = 2 * (dx * wx + dy * wy);
        const qc = dx * dx + dy * dy - minDist * minDist;
        if (qc <= 0) continue; // already overlapping -> resolveContacts handles it
        const disc = qb * qb - 4 * qa * qc;
        if (disc < 0) continue;
        const s = (-qb - Math.sqrt(disc)) / (2 * qa);
        if (s > eps && s <= maxT) best = Math.min(best, s);
      }
    }

    return best;
  }

  isSettled(): boolean {
    const c = this.constants;
    for (const b of this.balls) {
      if (Math.hypot(b.vel.x, b.vel.y) > c.linearSettle) return false;
      if (Math.abs(b.angVel.z) > c.angularSettle) return false;
      if (Math.abs(b.vel.z) > c.linearSettle || b.pos.z > b.radius + 1e-3) return false; // still airborne
    }
    return true;
  }

  snapshot(): Record<BallId, BallSnapshot> {
    const out = {} as Record<BallId, BallSnapshot>;
    for (const b of this.balls) out[b.id] = toSnapshot(b);
    return out;
  }

  frame(t: number): Frame {
    const balls = {} as Frame['balls'];
    for (const b of this.balls) balls[b.id] = { pos: { ...b.pos }, orientation: { ...b.orientation } };
    return { t, balls };
  }
}

/** Build a carom world from the shared config for a given variant. */
export function createCaromWorld(variant: VariantId, constants: PhysicsConstants = PHYSICS, ccd: boolean = USE_CCD): World {
  const spec = BALL_BY_VARIANT[variant];
  const radius = spec.diameter / 2;
  const table = tableFor(variant);
  const pos = startPositions(variant);
  return new World({
    playWidth: table.playWidth,
    playLength: table.playLength,
    constants,
    ccd,
    balls: ballsFor(variant).map((id) => ({ id, radius, mass: spec.mass, pos: pos[id] })),
  });
}
